import { motion } from 'framer-motion'
import Link from '../components/LocalizedLink'
import { FileSpreadsheet, Search, RefreshCw, FileCheck2, Download, ArrowRight } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'
import SEO from '../components/SEO'

const BASE = import.meta.env.BASE_URL

const translations = {
  el: {
    hero: {
      title: 'Πρόγραμμα Προσφορών για Μεταλλουργεία',
      subtitle: 'Δημιουργήστε, διαχειριστείτε και μετατρέψτε προσφορές σε προτιμολόγια απευθείας από το Workshop Assistant.',
      cta1: 'Δωρεάν Δοκιμή',
      cta2: 'Δείτε τις Λειτουργίες',
    },
    quotation: {
      title: 'Διαχείριση Προσφορών',
      body: 'Κάθε υπολογισμός κόστους στο Workshop Assistant μπορεί να μετατραπεί άμεσα σε επαγγελματική προσφορά με αναλυτική αναφορά Excel. Προβάλετε, αναζητήστε και διαχειριστείτε όλες τις προσφορές σας με ισχυρά φίλτρα ημερομηνίας, τιμής και υλικού.',
      items: [
        { icon: 'excel', title: 'Αναφορές Excel', description: 'Κάθε προσφορά συνοδεύεται από επαγγελματική αναφορά Excel με πλήρη ανάλυση κόστους.' },
        { icon: 'search', title: 'Αναζήτηση & Φίλτρα', description: 'Φιλτράρετε προσφορές κατά εύρος ημερομηνιών, τιμών ή υλικού για γρήγορη εύρεση.' },
        { icon: 'refresh', title: 'Επανυπολογισμός', description: 'Επανυπολογίστε μια υπάρχουσα προσφορά με διαφορετικό markup χωρίς να ξεκινήσετε από την αρχή.' },
      ],
    },
    proforma: {
      title: 'Δημιουργία Προτιμολογίου',
      body: 'Μόλις μια προσφορά γίνει αποδεκτή, το Workshop Assistant δημιουργεί ένα επίσημο προτιμολόγιο σε PDF απευθείας από αυτήν — έτοιμο να σταλεί στον πελάτη πριν την πληρωμή, χωρίς να χρειάζεται να ξαναγράψετε τα στοιχεία σε άλλο πρόγραμμα.',
    },
    faq: [
      { q: 'Πώς δημιουργείται μια προσφορά;', a: 'Κάθε υπολογισμός κόστους στο Cost Calculator μπορεί να αποθηκευτεί ως προσφορά με αναλυτική αναφορά Excel.' },
      { q: 'Τι είναι το προτιμολόγιο και πώς διαφέρει από την προσφορά;', a: 'Το προτιμολόγιο είναι ένα επίσημο έγγραφο PDF που δημιουργείται από μια αποδεκτή προσφορά, έτοιμο να σταλεί στον πελάτη πριν την πληρωμή.' },
      { q: 'Μπορώ να επανυπολογίσω μια παλιά προσφορά με νέο markup;', a: 'Ναι, η διαχείριση προσφορών σάς επιτρέπει να επανυπολογίσετε οποιαδήποτε προσφορά με διαφορετικό περιθώριο κέρδους.' },
    ],
    cta: { title: 'Δημιουργήστε την Πρώτη σας Προσφορά', subtitle: 'Κατεβάστε τη δωρεάν δοκιμή και δημιουργήστε προσφορές και προτιμολόγια σε λεπτά.', btn: 'Δωρεάν Δοκιμή' },
  },
  en: {
    hero: {
      title: 'Quotation Software for Fabrication Shops',
      subtitle: 'Create, manage, and turn quotes into proforma invoices directly from Workshop Assistant.',
      cta1: 'Download Free Trial',
      cta2: 'Explore Features',
    },
    quotation: {
      title: 'Offers Management',
      body: 'Every cost calculation in Workshop Assistant can be turned instantly into a professional quotation with a detailed Excel report. View, search, and manage all your quotes with powerful date, price, and material filters.',
      items: [
        { icon: 'excel', title: 'Excel Reports', description: 'Every quotation comes with a professional Excel report showing the full cost breakdown.' },
        { icon: 'search', title: 'Search & Filters', description: 'Filter quotes by date range, price range, or material for fast lookup.' },
        { icon: 'refresh', title: 'Recalculation', description: 'Recalculate an existing quote with a different markup without starting over.' },
      ],
    },
    proforma: {
      title: 'Proforma Invoice Creator',
      body: 'Once a quote is accepted, Workshop Assistant generates a formal proforma invoice PDF directly from it — ready to send to the customer ahead of payment, without re-entering the details into a separate program.',
    },
    faq: [
      { q: 'How is a quotation created?', a: 'Any cost calculation in the Cost Calculator can be saved as a quotation with a detailed Excel report.' },
      { q: "What's a proforma invoice and how is it different from a quote?", a: 'A proforma invoice is a formal PDF document generated from an accepted quote, ready to send to the customer ahead of payment.' },
      { q: 'Can I recalculate an old quote with a new markup?', a: 'Yes, offers management lets you recalculate any quote with a different profit margin.' },
    ],
    cta: { title: 'Create Your First Quotation', subtitle: 'Download the free trial and generate quotes and proforma invoices in minutes.', btn: 'Download Free Trial' },
  },
}

const icons = { excel: <FileSpreadsheet className="h-10 w-10" />, search: <Search className="h-10 w-10" />, refresh: <RefreshCw className="h-10 w-10" /> }

const QuotationSoftware = () => {
  const { lang } = useLanguage()
  const t = translations[lang]

  return (
    <div className="overflow-hidden">
      <SEO
        title="Quotation Software with Proforma Invoice Creator"
        description="Create quotes, manage offers, and generate proforma invoices for sheet metal fabrication jobs with Workshop Assistant."
        path="/quotation-software"
        faq={t.faq}
      />

      <section className="bg-gradient-to-br from-primary-600 to-primary-800 text-white">
        <div className="section-container">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-5xl md:text-6xl font-bold mb-6">{t.hero.title}</motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-xl opacity-90 mb-8">{t.hero.subtitle}</motion.p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/download" className="flex items-center justify-center space-x-2 px-8 py-4 bg-white text-primary-600 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-200 shadow-lg">
                <Download className="h-5 w-5" /><span>{t.hero.cta1}</span>
              </Link>
              <Link to="/features" className="flex items-center justify-center space-x-2 px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-all duration-200">
                <span>{t.hero.cta2}</span><ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section-container bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h2 className="text-3xl font-bold mb-4">{t.quotation.title}</h2>
            <p className="text-lg text-gray-600 mb-6">{t.quotation.body}</p>
            <ul className="space-y-3">
              {t.quotation.items.map((item, i) => (
                <li key={i} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 text-primary-600">{icons[item.icon]}</div>
                  <div>
                    <div className="font-semibold">{item.title}</div>
                    <div className="text-gray-600 text-sm">{item.description}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-2xl border border-gray-200">
            <img src={`${BASE}screenshots/offers.png?v=2`} alt={t.quotation.title} className="w-full h-auto object-cover" />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center bg-gray-50 rounded-2xl p-8 md:p-12">
          <div>
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 text-primary-600 rounded-xl mb-4">
              <FileCheck2 className="h-8 w-8" />
            </div>
            <h2 className="text-3xl font-bold mb-4">{t.proforma.title}</h2>
            <p className="text-lg text-gray-600">{t.proforma.body}</p>
          </div>
        </div>
      </section>

      <section className="section-container bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center">FAQ</h2>
          <div className="space-y-4">
            {t.faq.map((item, i) => (
              <details key={i} className="card group">
                <summary className="font-semibold cursor-pointer list-none flex justify-between items-center">
                  {item.q}
                  <span className="text-primary-600 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-gray-600 mt-3">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="section-container bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">{t.cta.title}</h2>
          <p className="text-xl mb-8 opacity-90">{t.cta.subtitle}</p>
          <Link to="/download" className="inline-flex items-center space-x-2 px-8 py-4 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-all duration-200 shadow-lg">
            <Download className="h-5 w-5" /><span>{t.cta.btn}</span>
          </Link>
        </div>
      </section>
    </div>
  )
}

export default QuotationSoftware
